import { PureComponent } from 'react'
import _ from 'lodash'
import cx from 'classnames'
import moment from 'moment'
import styles from './Shared.css'

import { numeralFormat, convertDateByMoment } from '../../../../lib/renderUtil'
import { SALE_STAT_KEY_MAP } from '../../../constants/globalTypes'
import { YCard, YCardTitle } from '../../Common/YCard'
import Popover from '../../Common/Popover'
import { DATE_FORMAT, TIME_DURATION } from '../../../lib/MomentHelper'
import {
  CARRIER,
  CARRIER_MAP
} from './constants/carrier'
import { PRODUCT_SHIP_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'

const OperatingStatsCardTitle = ({ title, content }) => {
  return (
    <YCardTitle
      title={title}
      className={styles.statCardTitle}
    >
      {content}
    </YCardTitle>
  )
}

const PopoverWrapper = ({ contentTitle, children }) =>
  contentTitle ? <Popover content={contentTitle}>{children}</Popover> : children

export const OperatingStatsCard = ({ title, titleContent, content, contentTitle, className }) => {
  return (
    <YCard
      fluid
      className={cx(
        styles.statCard,
        { [className]: !!className }
      )}
    >
      <OperatingStatsCardTitle
        title={title}
        content={titleContent}
      />
      <PopoverWrapper contentTitle={contentTitle}>
        <div className={styles.statCardItem}>
          {content}
        </div>
      </PopoverWrapper>
    </YCard>
  )
}

export const calculateDataSum = (data, dataKey) => {
  const sum = data.reduce((result, entry) => {
    const val = entry.value
    return result + (_.isNumber(val) ? val : 0)
  }, 0)
  return sum
}

export const calculateDataPercent = (sum, value) => {
  return (_.isNumber(value) ? value : 0) / sum
}

/** export components */
export class CustomTooltip extends PureComponent {
  renderContent = () => {
    const { payload, separator, itemStyle, itemSorter, valueFormatter } = this.props
    const listStyle = { padding: 0, margin: 0 }
    const items = payload.sort(itemSorter)
      .map((entry, i) => {
        const finalItemStyle = {
          display: 'block',
          paddingTop: 4,
          paddingBottom: 4,
          color: entry.color || '#000',
          ...itemStyle
        }

        const hasName = _.isString(entry.name)
        const formatedVal = valueFormatter ? valueFormatter(entry.value) : numeralFormat(entry.value)
        return (
          <li
            className='recharts-tooltip-item'
            key={`tooltip-item-${i}`}
            style={finalItemStyle}
          >
            {hasName ? <span className='recharts-tooltip-item-name'>{entry.name}</span> : null}
            {
              hasName
                ? <span className='recharts-tooltip-item-separator'>{separator}</span>
                : null
            }
            <span className='recharts-tooltip-item-value'>
              {formatedVal}
            </span>
          </li>
        )
      })

    return (
      <ul
        className='recharts-tooltip-item-list'
        style={listStyle}
      >
        {items}
      </ul>
    )
  }

  render () {
    const { labelStyle, label, wrapperStyle } = this.props
    const finalStyle = {
      margin: 0,
      padding: 10,
      backgroundColor: '#fff',
      border: '1px solid #ccc',
      whiteSpace: 'nowrap',
      ...wrapperStyle
    }
    const finalLabelStyle = {
      margin: 0,
      ...labelStyle
    }
    const finalLabel = label

    return (
      <div
        className='recharts-default-tooltip'
        style={finalStyle}
      >
        <p
          className='recharts-tooltip-label'
          style={finalLabelStyle}
        >
          {finalLabel}
        </p>
        {this.renderContent()}
      </div>
    )
  }
}

export class BaseStatHelper {
  constructor (conf) {
    this.conf = conf
  }

  getConf = () => {
    return this.conf
  }

  getSetting = (key) => {
    return _.get(this.getConf(), `${key}`, {})
  }

  getText = (key) => {
    return _.get(this.getConf(), `${key}.text`, '')
  }

  getTooltip = (key) => {
    return _.get(this.getConf(), `${key}.tooltip`, '')
  }

  getUrl = (key) => {
    return _.get(this.getConf(), `${key}.url`, '')
  }

  getI13nModel = (key) => {
    return _.get(this.getConf(), `${key}.i13nModel`, '')
  }
}

class SaleHelper extends BaseStatHelper {
  getSaleText = (key) => {
    return this.getText(key)
  }

  getSaleToolTip = (key) => {
    return this.getTooltip(key)
  }

  formatPeriodSegmentWithYear = periodSegment => {
    const format = 'YYYY/MM/DD'
    const {
      start,
      end
    } = periodSegment

    const formattedStartDate = convertDateByMoment(start, format)
    const formattedEndData = convertDateByMoment(end, format)

    return `${formattedStartDate} ~ ${formattedEndData}`
  }

  formatPeriodSegment = (periodSegment = {}, compareYear) => {
    let format = 'MM/DD'
    // 如果跟現在年份不一致則顯示年份
    if (compareYear && moment(periodSegment.start).isBefore(moment(), 'year')) {
      format = 'YYYY/MM/DD'
    }
    return `${convertDateByMoment(periodSegment.start, format)}~${convertDateByMoment(periodSegment.end, format)}`
  }

  prepareStatData = (data) => {
    return _.map(this.getConf(), (mapValue, mapKey) => {
      const foundStat = _.find(data, { key: mapKey })
      if (!foundStat) {
        switch (mapKey) {
          case 'periodOrderSubtotal':
            return { key: mapKey, value: SaleStatHelper.calculatePeriodOrderSubtotal(data) }
          case 'periodOrderAmountSubtotal':
            return { key: mapKey, value: SaleStatHelper.calculatePeriodOrderAmountSubtotal(data) }
          default:
            return null
        }
      }
      return foundStat
    })
  }

  calculatePeriodOrderSubtotal = (data) => {
    // 區間訂單數小計 = 訂單成立數 - 取消訂單數 - 申請退貨數
    const newOrderCount = _.find(data, { key: 'newOrderCount' })
    const canceledOrderCount = _.find(data, { key: 'canceledOrderCount' })
    const refundApplicationCount = _.find(data, { key: 'refundApplicationCount' })
    const baseValue = newOrderCount.value
    return _.reduce([canceledOrderCount, refundApplicationCount], (result, o, idx) => {
      result -= o.value
      return result
    }, baseValue)
  }

  calculatePeriodOrderAmountSubtotal = (data) => {
    // 區間訂單金額小計 = 訂單成立金額 - 取消訂單金額 - 申請退貨金額
    const newOrderAmount = _.find(data, { key: 'newOrderAmount' })
    const canceledOrderAmount = _.find(data, { key: 'canceledOrderAmount' })
    const refundApplicationAmount = _.find(data, { key: 'refundApplicationAmount' })
    const baseValue = newOrderAmount.value
    return _.reduce([canceledOrderAmount, refundApplicationAmount], (result, o, idx) => {
      result -= o.value
      return result
    }, baseValue)
  }
}

export const SaleStatHelper = new SaleHelper(SALE_STAT_KEY_MAP)

// 需要手動算下一個區間的參數
const TIME_DURATION_MANIPULATING_MAP = {
  [TIME_DURATION.DAY.value]: [1, 'days'],
  [TIME_DURATION.WEEK.value]: [6, 'days'],
  [TIME_DURATION.MONTH.value]: [1, 'months']
}

export const CHART_FIELD = {
  REPORT_DATE: 'reportDate',
  PAGE_VIEW: 'pageView'
}

export const SEARCH_FIELD = {
  MAIN_RANGE: 'mainRange',
  COMPARISON_RANGE: 'comparisonRange',
  LISTING_TYPE: 'listingType',
  LISTING_ID: 'listingId',
  CARRIERS: 'carriers'
}
export const COMPARISON_PREFIX = `${SEARCH_FIELD.COMPARISON_RANGE}-`

export const ONE_YEAR_MONTHS = 12

export const PAGE_VIEWS_API_LIMIT = 120

// 根據 reportDateStart, reportDateEnd 將 params 分隔月份
export const getParamsListByMonth = params => {
  const {
    reportDateStart,
    reportDateEnd
  } = params
  const startYear = moment(reportDateStart).get('year')
  const startMonth = moment(reportDateStart).get('month')
  const endYear = moment(reportDateEnd).get('year')
  const endMonth = moment(reportDateEnd).get('month')
  // 開始結束月份一樣則不需要轉換
  if (startMonth === endMonth) {
    return [params]
  }

  const monthRange = (
    (endMonth + (endYear * ONE_YEAR_MONTHS)) -
    (startMonth + (startYear * ONE_YEAR_MONTHS))
  )
  const paramsList = _.times(monthRange + 1, i => {
    if (i === monthRange) {
      const monthStartDate = moment(reportDateEnd).format(DATE_FORMAT.API_START_DATE)
      return {
        reportDateStart: monthStartDate,
        reportDateEnd: moment(reportDateEnd).format(DATE_FORMAT.API)
      }
    }
    const currentMonth = moment(reportDateStart)
      .add(i, 'month')
      .endOf('month')
    const monthEndDate = currentMonth.format(DATE_FORMAT.API)
    let monthStartDate = moment(reportDateStart).format(DATE_FORMAT.API)
    if (i !== 0) {
      monthStartDate = currentMonth.format(DATE_FORMAT.API_START_DATE)
    }
    return {
      reportDateStart: monthStartDate,
      reportDateEnd: monthEndDate
    }
  })
  return paramsList
}

export const checkIsCarrierPageView = (carrier, pageView) => {
  let isCarrierPageView = false
  switch (carrier) {
    case CARRIER.MOBILE_WEB:
    case CARRIER.DESKTOP_WEB:
    case CARRIER.APP:
    case CARRIER.UNKNOWN: {
      if (carrier === pageView.carrier) {
        isCarrierPageView = true
      }
      break
    }
    case CARRIER.ALL:
      isCarrierPageView = true
      break
    default: {
      break
    }
  }
  return isCarrierPageView
}

/**
 * 以 reportDate 分群 pageViews
 */
export const getPageViewsMapByTimeDuration = (pageViews, timeDuration) => {
  switch (timeDuration) {
    case TIME_DURATION.DAY.value:
      return _.groupBy(pageViews, CHART_FIELD.REPORT_DATE)
    case TIME_DURATION.WEEK.value: {
      // 以週分群，第一群是主要區間的起始日開始加 5 天共 6 天
      let startDate = _.get(pageViews, '0.reportDate')
      let range = [moment(startDate), moment(startDate).add(5, 'days')]
      return _.groupBy(pageViews, ({ reportDate }) => {
        const [start, end] = range
        if (
          start.isSameOrBefore(reportDate) &&
          end.isSameOrAfter(reportDate)
        ) {
          return startDate
        }
        startDate = moment(startDate).add(6, 'days').format(DATE_FORMAT.API)
        range = [
          moment(startDate),
          moment(startDate).add(5, 'days')
        ]
        return startDate
      })
    }
    case TIME_DURATION.MONTH.value:
      // 以月分群所以只看年月，日固定為 1 號
      return _.groupBy(pageViews, pageView => {
        return moment(pageView.reportDate).format(DATE_FORMAT.API_START_DATE)
      })
    default:
      return {}
  }
}

export const transformPageViewsToChartData = (pageViews, formValues, calculatedData) => {
  if (_.isEmpty(pageViews)) {
    return null
  }

  const isComparison = !_.isEmpty(calculatedData)
  const carriers = _.get(formValues, SEARCH_FIELD.CARRIERS, [])
  const timeDuration = _.get(formValues, 'timeDuration')
  let groupByCarrierChartData = {}
  // 手動填好預設勾選的裝置集合
  for (const carrier of carriers) {
    _.set(groupByCarrierChartData, carrier, {
      carrier,
      name: (
        isComparison
          ? `${COMPARISON_PREFIX}${CARRIER_MAP[carrier]}`
          : CARRIER_MAP[carrier]
      ),
      data: []
    })
  }

  const [mainChartData = []] = calculatedData
  const groupByDatePageViews = getPageViewsMapByTimeDuration(pageViews, timeDuration)

  // 以 reportDate 分群後把每一個日期區間的瀏覽量按照需要顯示在圖表上的裝置寫入
  _.forEach(groupByDatePageViews, (datePageViews, date) => {
    const groupByCarrierPageViews = {}
    for (const datePageView of datePageViews) {
      for (const carrier of carriers) {
        const isCarrierPageView = checkIsCarrierPageView(carrier, datePageView)
        if (isCarrierPageView) {
          if (carrier in groupByCarrierPageViews) {
            groupByCarrierPageViews[carrier].push(datePageView)
          } else {
            groupByCarrierPageViews[carrier] = [datePageView]
          }
        }
      }
    }

    groupByCarrierChartData = _.transform(groupByCarrierPageViews, (groupChartData, carrierPageViews, carrier) => {
      const summaryByCarrierPageView = _.flow(
        carrierPageViews => _.map(carrierPageViews, carrierPageView => (
          _.get(carrierPageView, CHART_FIELD.PAGE_VIEW, 0)
        )),
        _.sum
      )(carrierPageViews)
      // 從要寫入的集合 groupChartData 可以取得目前在圖表 X 軸的位置
      const currentIndex = _.size(
        _.get(groupChartData, `${carrier}.data`, [])
      )
      const carrierMainRangeReportDate = _.flow(
        chartData => _.find(chartData, { carrier }),
        currentCarrierData => {
          if (!isComparison) {
            return date
          }
          const currentReportDate = (
            // 對照主要日期區間的 X 軸位置寫入日期，這樣才能在不同日期區間對齊圖表 X 軸
            _.get(currentCarrierData, `data.${currentIndex}.reportDate`) ||
            // 如果主要日期找不到代表比較日期的區間已經超過主要日期，所以改成自己往後計算日期區間
            moment(
              _.get(groupChartData, `${carrier}.data.${currentIndex - 1}.reportDate`)
            )
              .add(...TIME_DURATION_MANIPULATING_MAP[timeDuration])
              .format(DATE_FORMAT.API)
          )
          return currentReportDate
        }
      )(mainChartData)
      groupChartData[carrier].data.push({
        pageView: summaryByCarrierPageView,
        correctReportDate: date,
        reportDate: carrierMainRangeReportDate,
        source: carrierPageViews
      })
      return groupChartData
    }, groupByCarrierChartData)
  })
  const chartData = _.values(groupByCarrierChartData)
  return chartData
}

export const transformPageViewsListToChartDataList = (pageViewsList, formValues) => {
  const chartDataList = _.flow(
    pageViewsList => _.reduce(pageViewsList, (calculatedData, pageViews) => {
      const data = transformPageViewsToChartData(pageViews, formValues, calculatedData)
      calculatedData.push(data)
      return calculatedData
    }, []),
    _.compact
  )(pageViewsList)
  return chartDataList
}

export const calendarDisableDate = (periodEndMaxDate, periodStart) => {
  const sixMonthsLaterData = moment(periodStart).add(180, 'days')
  const todayData = moment()
  const result = moment(sixMonthsLaterData).isBefore(todayData) ? sixMonthsLaterData : periodEndMaxDate
  return moment(result).endOf('day')
}

export const getListingImage = listing => {
  const mainImage = _.flow(
    () => _.get(listing, 'images', _.get(listing, 'models[0].images', [])),
    images => _.filter(images, image => image.order === 1 && image.height >= 1000)
  )()
  if (_.isEmpty(mainImage)) {
    return ''
  }
  return _.get(mainImage[0], 'url', '')
}

export const getCouponImage = (listing = {}) => {
  const { models, images: listingImages = [] } = listing
  const images = _.get(
    models,
    // 先找雙層屬性裡的首圖
    '0.items[0].images',
    // 沒有再找單層賣場、也沒有則預期為無屬性賣場從 listing.images 拿首圖
    _.get(models, '0.images', listingImages)
  )
  const imageUrl = _.flow(
    () => _.find(images, { order: 1, height: 250 }),
    targetImage => _.get(targetImage, 'url', '')
  )()
  return imageUrl
}

export const getAvailableCountLink = (sharedLink, shipType = {}, listingId) => {
  const link = shipType.id === PRODUCT_SHIP_TYPE.EXPRESS_24HR
    ? _.get(sharedLink, 'scmCreateInventory', '')
    : _.get(sharedLink, 'scmStockQtyAdjust', '')
  const availableCountLink = `${link}?gdid=${listingId}`
  return availableCountLink
}
